import { formatCountdown } from '@theme/utilities';

/** @type {string} */
const PERSIST_STORAGE_PREFIX = 'mool:countdown:';

/** @type {string} */
const SIDE_CART_RENDERED_EVENT = 'side-cart:rendered';

/** @type {string} */
const CART_PAGE_RENDERED_EVENT = 'cart-page:rendered';

/**
 * @typedef {Object} CountdownTimerState
 * @property {AbortController} abortController
 * @property {ReturnType<typeof setInterval> | null} interval
 * @property {ReturnType<typeof setInterval> | null} startCheckInterval
 * @property {Date | null} endTime
 * @property {Date | null} startTime
 * @property {boolean} hasAnimatedInitial
 * @property {boolean} hasInitializedProgress
 * @property {boolean} loopEnabled
 * @property {number | null} durationMs
 * @property {number | null} totalMs
 * @property {string | null} persistId
 * @property {string | null} durationStr
 */

/** @type {WeakMap<HTMLElement, CountdownTimerState>} */
const instances = new WeakMap();

/**
 * @param {string} persistId
 * @param {string} durationStr
 * @returns {Date | null}
 */
function readPersistedEndTime(persistId, durationStr) {
  try {
    const raw = sessionStorage.getItem(PERSIST_STORAGE_PREFIX + persistId);
    if (!raw) return null;

    const data = JSON.parse(raw);
    if (
      !data ||
      data.duration !== durationStr ||
      typeof data.endTime !== 'number'
    ) {
      return null;
    }

    const endTime = new Date(data.endTime);
    return Number.isNaN(endTime.getTime()) ? null : endTime;
  } catch {
    return null;
  }
}

/**
 * @param {string} persistId
 * @param {string} durationStr
 * @param {Date} endTime
 */
function writePersistedEndTime(persistId, durationStr, endTime) {
  try {
    sessionStorage.setItem(
      PERSIST_STORAGE_PREFIX + persistId,
      JSON.stringify({
        duration: durationStr,
        endTime: endTime.getTime(),
      })
    );
  } catch {
    // Ignore storage quota or privacy mode errors.
  }
}

/**
 * @param {string} persistId
 */
function clearPersistedEndTime(persistId) {
  try {
    sessionStorage.removeItem(PERSIST_STORAGE_PREFIX + persistId);
  } catch {
    // Ignore storage errors.
  }
}

/**
 * Parses a timestamp string into a Date object.
 * Supports "YYYY-MM-DD HH:MM:SS" (ISO-like) and "YYYY-MM-DD HH:MM AM/PM" formats.
 * @param {string} value
 * @returns {Date|null}
 */
function parseTimestamp(value) {
  if (!value || typeof value !== 'string') return null;

  const trimmed = value.trim();

  const ampmMatch = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i
  );
  if (ampmMatch) {
    const year = ampmMatch[1];
    const month = ampmMatch[2];
    const day = ampmMatch[3];
    const rawHrs = ampmMatch[4];
    const mins = ampmMatch[5];
    const secs = ampmMatch[6];
    const ampm = ampmMatch[7];
    if (!year || !month || !day || !rawHrs || !mins || !ampm) return null;

    let h = parseInt(rawHrs, 10);
    if (ampm.toLowerCase() === 'pm' && h !== 12) h += 12;
    else if (ampm.toLowerCase() === 'am' && h === 12) h = 0;
    const d = new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      h,
      parseInt(mins, 10),
      secs ? parseInt(secs, 10) : 0
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const normalized = trimmed.replace(' ', 'T');
  const ms = Date.parse(normalized);
  if (!Number.isNaN(ms)) return new Date(ms);

  return null;
}

/**
 * @param {string} durationStr
 * @returns {number}
 */
function parseDurationMs(durationStr) {
  const parts = durationStr.split(':').map((s) => parseInt(s, 10) || 0);

  if (parts.length >= 3) {
    const hours = parts[0] || 0;
    const minutes = parts[1] || 0;
    const seconds = parts[2] || 0;
    return (hours * 3600 + minutes * 60 + seconds) * 1000;
  }

  const first = parts[0] || 0;
  const second = parts[1] || 0;
  return parts.length >= 2 ? (first * 60 + second) * 1000 : first * 1000;
}

/**
 * @param {number} value
 * @returns {number}
 */
function clampPercent(value) {
  return Math.max(0, Math.min(100, value));
}

/**
 * @param {HTMLElement} root
 * @param {number} percent
 * @param {{ animate?: boolean }} [options]
 */
function updateCountdownProgress(root, percent, options = {}) {
  if (root.dataset.showProgress !== 'true') return;

  const container = root.closest('[data-sale-countdown]');
  const fill = container?.querySelector('[data-countdown-progress-fill]');
  const progressbar = container?.querySelector('[role="progressbar"]');
  const clamped = clampPercent(percent);
  const shouldAnimate = options.animate !== false;

  if (fill instanceof HTMLElement) {
    if (!shouldAnimate) {
      fill.style.transition = 'none';
    }

    fill.style.width = `${clamped}%`;

    if (!shouldAnimate) {
      fill.offsetHeight;
      fill.style.transition = '';
    }
  }

  if (progressbar instanceof HTMLElement) {
    progressbar.setAttribute('aria-valuenow', String(Math.round(clamped)));
  }
}

/**
 * @param {HTMLElement} root
 */
function destroyCountdownTimer(root) {
  const state = instances.get(root);
  if (!state) return;

  state.abortController.abort();

  if (state.interval) {
    clearInterval(state.interval);
  }

  if (state.startCheckInterval) {
    clearInterval(state.startCheckInterval);
  }

  instances.delete(root);
}

/**
 * @param {HTMLElement} root
 */
function initCountdownTimer(root) {
  if (!(root instanceof HTMLElement)) return;

  destroyCountdownTimer(root);

  const abortController = new AbortController();

  /** @type {CountdownTimerState} */
  const state = {
    abortController,
    interval: null,
    startCheckInterval: null,
    endTime: null,
    startTime: null,
    hasAnimatedInitial: false,
    hasInitializedProgress: false,
    loopEnabled: false,
    durationMs: null,
    totalMs: null,
    persistId: root.dataset.persistId || null,
    durationStr: root.dataset.duration || null,
  };

  instances.set(root, state);

  const display = root.querySelector('[data-countdown-timer-display]');
  const notStartedMessage = root.querySelector(
    '.countdown-timer__message--not-started'
  );
  const expiredMessage = root.querySelector(
    '.countdown-timer__message--expired'
  );

  function persistEndTime() {
    if (!state.persistId || !state.durationStr || !state.endTime) return;
    writePersistedEndTime(state.persistId, state.durationStr, state.endTime);
  }

  function showDisplay() {
    if (display instanceof HTMLElement) {
      display.hidden = false;
    }

    if (notStartedMessage instanceof HTMLElement) {
      notStartedMessage.hidden = true;
    }

    if (expiredMessage instanceof HTMLElement) {
      expiredMessage.hidden = true;
    }
  }

  function showNotStartedMessage() {
    if (display instanceof HTMLElement) {
      display.hidden = true;
    }

    if (notStartedMessage instanceof HTMLElement) {
      notStartedMessage.hidden = false;
    }

    if (expiredMessage instanceof HTMLElement) {
      expiredMessage.hidden = true;
    }

    updateCountdownProgress(root, 0, { animate: false });
    state.hasInitializedProgress = false;
  }

  /**
   * @param {number} elapsedPercent
   */
  function setProgressPercent(elapsedPercent) {
    if (!state.hasInitializedProgress) {
      state.hasInitializedProgress = true;
      requestAnimationFrame(() => {
        updateCountdownProgress(root, elapsedPercent);
      });
      return;
    }

    updateCountdownProgress(root, elapsedPercent);
  }

  function cleanupIntervals() {
    if (state.interval) {
      clearInterval(state.interval);
      state.interval = null;
    }

    if (state.startCheckInterval) {
      clearInterval(state.startCheckInterval);
      state.startCheckInterval = null;
    }
  }

  /**
   * @param {{ days: string, hours: string, minutes: string, seconds: string, total: number }} countdown
   * @param {boolean} [animate]
   */
  function renderDigits(countdown, animate = true) {
    Object.entries(countdown).forEach(([unit, value]) => {
      if (unit === 'total') return;
      const segment = root.querySelector(`[data-unit="${unit}"]`);
      if (!segment) return;
      const el = segment.querySelector('.countdown-timer__value');
      if (!el) return;

      const nextValue = String(value);

      if (!animate || el.textContent === nextValue) {
        el.textContent = nextValue;
        return;
      }

      animateNumber(el, nextValue);
    });
  }

  /**
   * @param {{ days: string, hours: string, minutes: string, seconds: string, total: number }} countdown
   */
  function animateInitial(countdown) {
    renderDigits(countdown, true);
  }

  /**
   * @param {{ days: string, hours: string, minutes: string, seconds: string }} digits
   */
  function updateDigits({ days, hours, minutes, seconds }) {
    renderDigits({ days, hours, minutes, seconds, total: 0 }, true);
  }

  /**
   * @param {Element} el
   * @param {string} newValue
   */
  function animateNumber(el, newValue) {
    const oldValue = el.textContent;
    if (oldValue === newValue) return;

    const segment = el.closest('.countdown-timer__segment');
    if (segment) {
      segment.classList.remove('is-bouncing');
      requestAnimationFrame(() => {
        segment.classList.add('is-bouncing');
      });
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'digit-roll';

    const oldSpan = document.createElement('span');
    oldSpan.textContent = oldValue;

    const newSpan = document.createElement('span');
    newSpan.textContent = newValue;

    wrapper.appendChild(oldSpan);
    wrapper.appendChild(newSpan);

    el.textContent = '';
    el.appendChild(wrapper);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        wrapper.classList.add('animate');
      });
    });

    setTimeout(() => {
      el.textContent = newValue;
    }, 500);
  }

  function advanceToNextCycle() {
    if (!state.endTime) return;

    const now = new Date();

    if (state.durationMs) {
      while (state.endTime <= now) {
        state.endTime = new Date(state.endTime.getTime() + state.durationMs);
      }
      persistEndTime();
      return;
    }

    const oneDayMs = 24 * 60 * 60 * 1000;

    while (state.endTime <= now) {
      state.endTime = new Date(state.endTime.getTime() + oneDayMs);

      if (state.startTime) {
        state.startTime = new Date(state.startTime.getTime() + oneDayMs);
      }
    }
  }

  function showExpiredState() {
    if (display instanceof HTMLElement) {
      display.hidden = true;
    }

    if (notStartedMessage instanceof HTMLElement) {
      notStartedMessage.hidden = true;
    }

    if (expiredMessage instanceof HTMLElement) {
      expiredMessage.hidden = false;
    }

    updateCountdownProgress(root, 100);

    if (root.dataset.removeOnExpire === 'true') {
      const slide = root.closest('.slideshow__slide');
      const announcementBar = root.closest('[data-announcement-bar]');

      if (slide && announcementBar) {
        slide.remove();
        announcementBar.dispatchEvent(
          new CustomEvent('announcement-bar:refresh', { bubbles: true })
        );
      }
    }
  }

  /**
   * @param {Date} startTime
   */
  function waitForStart(startTime) {
    state.startCheckInterval = setInterval(() => {
      if (new Date() >= startTime) {
        cleanupIntervals();
        state.hasAnimatedInitial = false;
        showDisplay();
        startCountdown();
      }
    }, 1000);
  }

  function startCountdown() {
    const tick = () => {
      if (!state.endTime) return;

      const remaining = state.endTime.getTime() - Date.now();

      if (remaining <= 0) {
        if (state.loopEnabled) {
          advanceToNextCycle();

          if (state.startTime) {
            state.totalMs = state.endTime.getTime() - state.startTime.getTime();
          } else if (state.durationMs) {
            state.totalMs = state.durationMs;
          }

          state.hasInitializedProgress = false;
          updateCountdownProgress(root, 0, { animate: false });
          const now = new Date();

          if (state.startTime && now < state.startTime) {
            cleanupIntervals();
            state.hasAnimatedInitial = false;
            showNotStartedMessage();
            waitForStart(state.startTime);
            return;
          }
        } else {
          showExpiredState();
          cleanupIntervals();
          return;
        }
      }

      const currentRemaining = Math.max(
        0,
        state.endTime.getTime() - Date.now()
      );

      if (state.totalMs && state.totalMs > 0) {
        const elapsed = state.totalMs - currentRemaining;
        const elapsedPercent = (elapsed / state.totalMs) * 100;
        setProgressPercent(elapsedPercent);
      }

      const countdown = formatCountdown(currentRemaining);

      if (!state.hasAnimatedInitial) {
        // Skip digit-roll on cart re-boot so AJAX updates feel instant.
        const animate = !state.persistId;
        renderDigits(countdown, animate);
        state.hasAnimatedInitial = true;
      } else {
        updateDigits(countdown);
      }
    };

    state.interval = setInterval(tick, 1000);
    tick();
  }

  function initialize() {
    state.loopEnabled = root.dataset.loop === 'true';

    const durationStr = root.dataset.duration;
    if (durationStr) {
      const durationMs = parseDurationMs(durationStr);
      if (!durationMs) return;

      state.loopEnabled = true;
      state.durationMs = durationMs;
      state.totalMs = durationMs;
      state.durationStr = durationStr;

      if (state.persistId) {
        const persistedEndTime = readPersistedEndTime(
          state.persistId,
          durationStr
        );
        const now = Date.now();

        if (persistedEndTime && persistedEndTime.getTime() > now) {
          state.endTime = persistedEndTime;
        } else if (persistedEndTime) {
          const cycles = Math.floor(
            (now - persistedEndTime.getTime()) / durationMs
          );
          state.endTime = new Date(
            persistedEndTime.getTime() + (cycles + 1) * durationMs
          );
          persistEndTime();
        } else {
          state.endTime = new Date(now + durationMs);
          persistEndTime();
        }
      } else {
        state.endTime = new Date(Date.now() + durationMs);
      }

      startCountdown();
      return;
    }

    const startTimeStr = root.dataset.startTime;
    const endTimeStr = root.dataset.time || root.dataset.endTime;

    state.startTime = startTimeStr ? parseTimestamp(startTimeStr) : null;
    state.endTime = endTimeStr ? parseTimestamp(endTimeStr) : null;

    if (!state.endTime) {
      showExpiredState();
      return;
    }

    if (state.startTime) {
      state.totalMs = state.endTime.getTime() - state.startTime.getTime();
    } else {
      const oneDayMs = 24 * 60 * 60 * 1000;
      state.totalMs = oneDayMs;
    }

    const now = new Date();

    if (state.startTime && now < state.startTime) {
      showNotStartedMessage();
      waitForStart(state.startTime);
      return;
    }

    if (now >= state.endTime) {
      if (state.loopEnabled) {
        advanceToNextCycle();
        const now2 = new Date();

        if (state.startTime && now2 < state.startTime) {
          showNotStartedMessage();
          waitForStart(state.startTime);
          return;
        }
      } else {
        showExpiredState();
        return;
      }
    }

    startCountdown();
  }

  initialize();
}

/**
 * @param {Document | Element} scope
 */
function bootCountdownTimers(scope) {
  scope.querySelectorAll('[data-countdown-timer]').forEach((root) => {
    if (root instanceof HTMLElement) {
      initCountdownTimer(root);
    }
  });
}

/**
 * @param {Document | Element} scope
 */
function destroyCountdownTimersInScope(scope) {
  scope.querySelectorAll('[data-countdown-timer]').forEach((root) => {
    if (root instanceof HTMLElement) {
      destroyCountdownTimer(root);
    }
  });
}

/**
 * Clears persisted cart timer state when the cart is empty.
 * @param {Element} scope
 */
function syncCartLimitedTimerPersistence(scope) {
  const sideCart =
    scope.id === 'side-cart' ? scope : scope.querySelector('#side-cart');
  const cartPage = scope.classList.contains('cart-page')
    ? scope
    : scope.querySelector('.cart-page');

  if (sideCart) {
    const itemCount =
      parseInt(sideCart.getAttribute('data-item-count') || '0', 10) || 0;
    if (itemCount <= 0) {
      clearPersistedEndTime('cart-limited-timer');
      return;
    }
  }

  if (cartPage?.querySelector('.cart-page__empty')) {
    clearPersistedEndTime('cart-limited-timer');
  }
}

/**
 * @param {Event} event
 */
function onCartSurfaceRendered(event) {
  const detail = event instanceof CustomEvent ? event.detail : null;
  const target =
    detail && detail.target instanceof Element ? detail.target : event.target;

  if (!(target instanceof Element)) return;

  syncCartLimitedTimerPersistence(target);
  bootCountdownTimers(target);
}

bootCountdownTimers(document);

document.addEventListener(SIDE_CART_RENDERED_EVENT, onCartSurfaceRendered);
document.addEventListener(CART_PAGE_RENDERED_EVENT, onCartSurfaceRendered);

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    bootCountdownTimers(event.target);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (event.target instanceof Element) {
    destroyCountdownTimersInScope(event.target);
  }
});
